// adminResponseSla.mjs — /admin/response-sla 的纯逻辑（SB-190 §7.3，第二百四十六批）。
//
// ── 这一页回答的问题 ────────────────────────────────────────────────────────
// 03-scoring D4 ①（响应时限，10 分）：期间内要求具名当事人响应的每一件事项，是不是在
// 1 个工作日内得到了首次实质响应。读面（后端 `ResponseSlaStore::report`）现算时钟、
// 列出候选；**「哪一条算实质」由人裁**，这一页就是管理员落裁决的地方（另一处是工作空间
// 的 `scripts/sla/rule.mjs`）。承接方在 dc-cli `sla mine` 看到的行与这里**逐字同形**
// （设计 §0-12）——所以这里不做任何归纳，只把行摆开、把裁决表单按 kind 收窄。
//
// ── 为什么这些判断必须是纯函数 ──────────────────────────────────────────────
//   · 分数：`needsRuling > 0` 时必须显示**区间**（设计 §5.3、§9 前端行）——一个把未裁
//     事项藏起来的单一数字不是读数，而这条只有在函数上才钉得住；
//   · 裁决表单：每种 kind 要什么、不许带什么（b246 §2.7）在服务端是硬拒；这里镜像一份
//     是为了不让人填完一段理由才被拒，且**不适用的键绝不进载荷**（静默丢弃等于让人以为
//     它生效了）；
//   · 时间输入：datetime-local 没有时区，复用 adminLogs 的 localInputToMs——"少一个 Z 差
//     八小时"只该有一份修法。
// 页面只渲染（SB-042：`.jsx` 里不放判据）。
import { localInputToMs } from './adminLogs.mjs';

// 服务端值域的副本（DP-2 的既有姿态）。值域外的行**不丢**：这是打分的读面，丢一行等于
// 把一件事项从分母里抹掉；处置是标签退回原串。
// SB-190 步 5（第二百四十七批）：state / rule 两族的值域与标签键（`sla.state.*` /
// `sla.rule.*`）搬到 responseSla.mjs，由成员面的钟点条与本页共用——两份标签迟早对不上。
// 这里 re-export 是为了本页与其测试的 import 面不变；kind 族（裁决种类）是管理员词汇，留在这里。
export { SLA_STATES, SLA_RULES, stateKey, ruleKey } from './responseSla.mjs';
export const SLA_CHANNELS = Object.freeze(['assigned', 'info_requested', 'dm']);
export const RULING_KINDS = Object.freeze(['substantive', 'not_substantive', 'provisional',
  'honoured', 'not_honoured', 'extend', 'exclude']);
export const CANDIDATE_KINDS = Object.freeze(['feedback', 'transition', 'dm']);
// 与 store 同一条（设计 §5.2：reason 必填非空、≤ 2000 字节）。
export const REASON_MAX_BYTES = 2000;
// 与 defaults::kSlaExtendMaxDays 同数：一条 extend 最多顺延 366 个工作日（服务端硬拒；这里镜像
// 是为了不让人填完一段理由才被拒）。
export const EXTEND_MAX_DAYS = 366;
// 与 store 的 ticket_no_shaped 同形：阻塞单只能是工单编号（大写前缀-数字），不是一段话。
export const BLOCKER_REF_RE = /^[A-Z]{2,8}-\d{1,12}$/;

export const kindKey = (k) => (RULING_KINDS.includes(k) ? `admin.responseSla.kind.${k}` : null);

// ── 查询 ────────────────────────────────────────────────────────────────────
// 表单 → /api/admin/response-sla 的参数。responders 是逗号 id 串；空值一律**不发**
// （client.mjs 的 qs() 跳过 undefined/''；不发 responder = 全部响应方，页面会标出来）。
export function parseResponders(text) {
  const parts = String(text ?? '').split(/[,\s，、]+/).map((s) => s.trim()).filter(Boolean);
  const ids = [];
  const bad = [];
  for (const p of parts) {
    if (/^\d+$/.test(p) && Number(p) > 0) ids.push(Number(p));
    else bad.push(p);
  }
  return { ids, bad };
}

export function slaQuery(form = {}) {
  const q = {};
  const from = localInputToMs(form.from);
  const to = localInputToMs(form.to);
  if (from > 0) q.fromMs = from;
  if (to > 0) q.toMs = to;
  const { ids } = parseResponders(form.responders);
  if (ids.length) q.responder = ids.join(',');
  return q;
}

// 本地就能判定的错，不必往返服务端：起止写反、响应方里有不是 id 的东西。
// 返回 i18n 键（文案归词典，判据归这里）。
export function filterProblems(form = {}) {
  const out = [];
  const from = localInputToMs(form.from);
  const to = localInputToMs(form.to);
  if (from > 0 && to > 0 && to <= from) out.push('admin.responseSla.err.range');
  if (parseResponders(form.responders).bad.length) out.push('admin.responseSla.err.responders');
  return out;
}

// ── 归一化 ──────────────────────────────────────────────────────────────────
const num = (v, dflt = 0) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? dflt : Number(v));
const str = (v) => (typeof v === 'string' ? v : (v === null || v === undefined ? '' : String(v)));

export function normalizeCandidate(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const ref = num(raw.ref, 0);
  if (!ref) return null;
  return {
    kind: str(raw.kind), ref,
    atMs: num(raw.atMs, 0), at: str(raw.at),
    strict: raw.strict === true, op: str(raw.op),
    key: `${str(raw.kind)}:${ref}`,
  };
}

export function normalizeRuling(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = num(raw.id, 0);
  if (!id) return null;
  return {
    id, kind: str(raw.kind),
    candidateKind: raw.candidateKind == null ? null : str(raw.candidateKind),
    candidateId: raw.candidateId == null ? null : num(raw.candidateId, 0),
    promisedByMs: raw.promisedByMs == null ? null : num(raw.promisedByMs, 0),
    extendDays: raw.extendDays == null ? null : num(raw.extendDays, 0),
    blockerRef: str(raw.blockerRef),
    // 裁决人自己的话：唯一被投影的人写文本（反馈/站内信正文永不在此）。纯文本渲染。
    reason: str(raw.reason),
    actorKind: str(raw.actorKind),
    actorId: raw.actorId == null ? null : num(raw.actorId, 0),
    viaAgent: raw.viaAgent === true,
    atMs: num(raw.atMs, 0), at: str(raw.at),
    deadlineSeenMs: num(raw.deadlineSeenMs, 0), ruleSeen: str(raw.ruleSeen),
  };
}

// 一行 = 一件事项。没有 itemRef 或 channel 的行裁不了，整条丢弃（唯一丢行的理由）。
export function normalizeItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const itemRef = num(raw.itemRef, 0);
  const channel = str(raw.channel);
  if (!itemRef || !channel) return null;
  return {
    key: `${channel}:${itemRef}`,
    channel, itemRef, ref: str(raw.ref) || String(itemRef),
    issuerId: num(raw.issuerId, 0), responderId: num(raw.responderId, 0),
    markedAtMs: num(raw.markedAtMs, 0), markedAt: str(raw.markedAt), markedAtUtc: str(raw.markedAtUtc),
    baseDay: num(raw.baseDay, 0), sameDayRank: num(raw.sameDayRank, 0), rule: str(raw.rule),
    startMs: num(raw.startMs, 0), deadlineMs: num(raw.deadlineMs, 0),
    deadlineAt: str(raw.deadlineAt), deadlineAtUtc: str(raw.deadlineAtUtc),
    effectiveDeadlineMs: num(raw.effectiveDeadlineMs, 0), effectiveDeadlineAt: str(raw.effectiveDeadlineAt),
    deadlineFrozen: raw.deadlineFrozen === true,
    candidates: (Array.isArray(raw.candidates) ? raw.candidates : []).map(normalizeCandidate).filter(Boolean),
    rulings: (Array.isArray(raw.rulings) ? raw.rulings : []).map(normalizeRuling).filter(Boolean),
    state: str(raw.state), excludedBy: str(raw.excludedBy),
    witnessMismatch: raw.witnessMismatch === true,
  };
}

// 整份报告。每个计数都有缺省（0），分数缺省 10（分母 0 ⇒ 满分是服务端的口径）。
export function normalizeReport(body) {
  const b = body && typeof body === 'object' ? body : {};
  const cal = b.calendar && typeof b.calendar === 'object' ? b.calendar : {};
  const range = Array.isArray(b.scoreRange) ? b.scoreRange : [];
  const score10 = num(b.score10, 10);
  return {
    asOf: str(b.asOf), from: str(b.from), fromUtc: str(b.fromUtc), to: str(b.to), toUtc: str(b.toUtc),
    responders: (Array.isArray(b.responders) ? b.responders : []).map((x) => num(x, 0)).filter(Boolean),
    calendar: {
      tz: str(cal.tz),
      holidays: (Array.isArray(cal.holidays) ? cal.holidays : []).map(str).filter(Boolean),
      threshold: num(cal.threshold, 0), workingDays: num(cal.workingDays, 0),
    },
    denominator: num(b.denominator), numerator: num(b.numerator), needsRuling: num(b.needsRuling),
    pending: num(b.pending), excluded: num(b.excluded), integrity: num(b.integrity),
    score10, scoreRaw: num(b.scoreRaw, score10),
    scoreRange: [num(range[0], score10), num(range[1], score10)],
    items: (Array.isArray(b.items) ? b.items : []).map(normalizeItem).filter(Boolean),
  };
}

// ── 分数 ────────────────────────────────────────────────────────────────────
// `needsRuling > 0` ⇒ 区间 "lo – hi"；否则单一数字。一位小数（服务端已 round，这里只定形）。
// 变异靶（设计 §9 前端行）：把区间换成单一数字 ⇒ 未裁事项被藏起来。
const one = (x) => (Number.isFinite(x) ? x.toFixed(1) : '—');
export function scoreText(report) {
  const r = report && typeof report === 'object' ? report : {};
  const needs = num(r.needsRuling);
  const range = Array.isArray(r.scoreRange) ? r.scoreRange : [];
  if (needs > 0) return `${one(num(range[0], NaN))} – ${one(num(range[1], NaN))}`;
  return one(num(r.score10, NaN));
}

// ── 裁决表单 ────────────────────────────────────────────────────────────────
// kind → 哪些字段适用（镜像 b246 §2.7 / 设计 §5.2）。null = 未知 kind（表单不出字段）。
export function rulingFormState(kind) {
  switch (kind) {
    case 'substantive':
    case 'not_substantive':
    case 'honoured':
    case 'not_honoured':
      return { candidate: true, promisedBy: false, extend: false };
    case 'provisional':
      return { candidate: true, promisedBy: true, extend: false };
    case 'extend':
      return { candidate: false, promisedBy: false, extend: true };
    case 'exclude':
      return { candidate: false, promisedBy: false, extend: false };
    default:
      return null;
  }
}

// 可选候选。honoured / not_honoured 只能指向**已裁为 provisional** 的候选（服务端硬拒）
// ——下拉里只列那些，免得人选完才被拒。
export function candidateOptions(row, kind) {
  const cands = Array.isArray(row?.candidates) ? row.candidates : [];
  if (kind !== 'honoured' && kind !== 'not_honoured') return cands;
  const provisional = new Set((row?.rulings ?? [])
    .filter((r) => r.kind === 'provisional' && r.candidateKind && r.candidateId)
    .map((r) => `${r.candidateKind}:${r.candidateId}`));
  return cands.filter((c) => provisional.has(c.key));
}

// 表单 → 问题清单与可提交性。理由与 escalations 的处置说明同一条：空的按不动。
export function rulingProblems(form = {}, toMs = localInputToMs) {
  const problems = {};
  const shape = rulingFormState(form.kind);
  if (!shape) problems.kind = 'required';
  else {
    if (shape.candidate && !/^[a-z]+:\d+$/.test(String(form.candidate ?? ''))) problems.candidate = 'required';
    if (shape.promisedBy && !(toMs(form.promisedBy) > 0)) problems.promisedBy = 'required';
    if (shape.extend) {
      const d = Number(form.extendDays);
      if (!Number.isInteger(d) || d <= 0) problems.extendDays = 'positive';
      else if (d > EXTEND_MAX_DAYS) problems.extendDays = 'range';
      const b = String(form.blockerRef ?? '').trim();
      if (!b) problems.blockerRef = 'required';
      else if (!BLOCKER_REF_RE.test(b)) problems.blockerRef = 'shape';
    }
  }
  const reason = String(form.reason ?? '');
  const trimmed = reason.trim();
  if (!trimmed) problems.reason = 'empty';
  else if (utf8Bytes(trimmed) > REASON_MAX_BYTES) problems.reason = 'tooLong';
  return { problems, canSubmit: Object.keys(problems).length === 0 };
}

// 载荷。**只带适用的键**：服务端对不适用的字段是拒，不是忽略——这里若把空串/0 一起
// 送过去，人看到的会是一句关于 extendDays 的 400，而他根本没碰那个框。
export function rulingPayload(row, form = {}, toMs = localInputToMs) {
  const shape = rulingFormState(form.kind) ?? { candidate: false, promisedBy: false, extend: false };
  const p = { channel: str(row?.channel), itemRef: num(row?.itemRef, 0), kind: str(form.kind) };
  if (shape.candidate) {
    const m = /^([a-z]+):(\d+)$/.exec(String(form.candidate ?? ''));
    if (m) { p.candidateKind = m[1]; p.candidateId = Number(m[2]); }
  }
  if (shape.promisedBy) {
    const ms = toMs(form.promisedBy);
    if (ms > 0) p.promisedByMs = ms;
  }
  if (shape.extend) {
    const d = Number(form.extendDays);
    if (Number.isInteger(d) && d > 0) p.extendDays = d;
    const b = String(form.blockerRef ?? '').trim();
    if (b) p.blockerRef = b;
  }
  p.reason = String(form.reason ?? '').trim();
  return p;
}

// 端点错误码 → i18n 键。RULE_VIOLATION 的服务端报文点名了病因（"provisional needs
// promisedByMs"），页面把它原样附在后面；NOT_FOUND 是事项已不在（窗口外或库变了）。
export const RULING_ERR_KEYS = Object.freeze({
  RULE_VIOLATION: 'admin.responseSla.err.RULE_VIOLATION',
  NOT_FOUND: 'admin.responseSla.err.NOT_FOUND',
});

function utf8Bytes(s) {
  let n = 0;
  for (const ch of String(s)) {
    const c = ch.codePointAt(0);
    n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }
  return n;
}
